import React, { useEffect, useRef, useState } from 'react';
import { Check } from '@/components/Icons/Check';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import type { MailSearchType } from '@/types/components';
import type { MailFolderSearchType } from '@/types/mail';
import { tv } from '@/utils/tv';

const chevron = tv({
  base: 'size-4 text-primary transition-transform duration-200',
  variants: {
    isOpen: {
      true: 'rotate-180',
      false: '',
    },
  },
});

const option = tv({
  base: 'flex h-[42px] cursor-pointer items-center justify-between gap-2 rounded-xl pr-2 pl-3',
  variants: {
    isSelected: {
      true: 'bg-info-surface',
      false: 'hover:bg-hover',
    },
  },
});

const optionLabel = tv({
  base: 'whitespace-nowrap',
  variants: {
    isSelected: {
      true: 'text-subhead-14 text-primary',
      false: 'text-body-14 text-foreground',
    },
  },
});

interface Props {
  types: MailFolderSearchType[];
  value: MailSearchType | null;
  onChange: (type: MailSearchType) => void;
}

export const MailSearchTypeDropdown: React.FC<Props> = ({
  types,
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedType = types.find(({ type }) => type === value);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (type: MailSearchType) => {
    onChange(type);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-fit shrink-0">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`text-caption-13 flex h-8 cursor-pointer items-center gap-1 rounded-3xl border border-transparent pr-2 pl-3 text-primary transition-colors contrast:border-border ${isOpen ? 'bg-background' : 'bg-card hover:bg-info-surface'}`}
      >
        {selectedType?.name}
        <ChevronDown className={chevron({ isOpen })} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 z-10 mt-1 flex w-fit min-w-[140px] flex-col gap-1 rounded-[14px] border border-transparent bg-popover p-2 shadow-[0px_3px_8px_rgba(18,31,55,0.08)] contrast:border-border">
          {types.map(({ type, name }) => {
            const isSelected = type === value;

            return (
              <button
                key={type}
                type="button"
                onClick={() => handleSelect(type)}
                className={option({ isSelected })}
              >
                <span className={optionLabel({ isSelected })}>{name}</span>
                {isSelected && (
                  <Check className="size-4 shrink-0 text-primary" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
